/* This file adds the requisite utility classes for Tailwind to work. */
@import 'tailwindcss';

@plugin 'tailwindcss-animate';

@custom-variant dark (@media (prefers-color-scheme: dark));

@theme {
  --color-border: hsl(var(--border));
  --color-input: hsl(var(--input));
  --color-ring: hsl(var(--ring));
  --color-background: hsl(var(--background));
  --color-foreground: hsl(var(--foreground));

  --color-primary: hsl(var(--primary));
  --color-primary-foreground: hsl(var(--primary-foreground));

  --color-secondary: hsl(var(--secondary));
  --color-secondary-foreground: hsl(var(--secondary-foreground));

  --color-destructive: hsl(var(--destructive));
  --color-destructive-foreground: hsl(var(--destructive-foreground));

  --color-muted: hsl(var(--muted));
  --color-muted-foreground: hsl(var(--muted-foreground));

  --color-accent: hsl(var(--accent));
  --color-accent-foreground: hsl(var(--accent-foreground));

  --color-popover: hsl(var(--popover));
  --color-popover-foreground: hsl(var(--popover-foreground));

  --color-card: hsl(var(--card));
  --color-card-foreground: hsl(var(--card-foreground));

  --color-success: hsl(var(--success));
  --color-success-foreground: hsl(var(--success-foreground));

  --color-warning: hsl(var(--warning));
  --color-warning-foreground: hsl(var(--warning-foreground));

  --radius-lg: var(--radius);
  --radius-md: calc(var(--radius) - 2px);
  --radius-sm: calc(var(--radius) - 4px);
}

/*
  The default border color has changed to `currentcolor` in Tailwind CSS v4,
  so we've added these compatibility styles to make sure everything still
  looks the same as it did with Tailwind CSS v3.

  If we ever want to remove these styles, we need to add an explicit border
  color utility to any element that depends on these defaults.
*/
@layer base {
  *,
  ::after,
  ::before,
  ::backdrop,
  ::file-selector-button {
    border-color: var(--color-gray-200, currentcolor);
  }
}

@utility no-scrollbar {
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/*
 * --- Palette sync ---
 * Match docs/src/css/custom.css (Docusaurus / Infima).
 * HSL values are unwrapped for Tailwind: hsl(var(--token)).
 * Light primary ≈ #1a73e8; dark primary ≈ #8ab4f8.
 */

@layer base {
  html {
    scroll-behavior: smooth;
  }

  :root {
    --background: 210 40% 98%;
    --foreground: 222 47% 11%;
    --muted: 210 40% 96%;
    /* Slightly darker than before for clearer body/hero subcopy on pale bg */
    --muted-foreground: 215 24% 34%;
    --popover: 0 0% 100%;
    --popover-foreground: 222 47% 11%;
    --border: 214 20% 90%;
    --input: 214 20% 90%;
    --card: 0 0% 100%;
    --card-foreground: 222 47% 11%;
    /* #1a73e8 — accessible on white; pairs with Google Sign-In chrome */
    --primary: 217 89% 51%;
    --primary-foreground: 0 0% 100%;
    --secondary: 217 60% 94%;
    --secondary-foreground: 222 47% 20%;
    --accent: 217 70% 94%;
    --accent-foreground: 217 60% 28%;
    --destructive: 0 72% 51%;
    --destructive-foreground: 210 40% 98%;
    --success: 142 76% 36%;
    --success-foreground: 0 0% 100%;
    --warning: 35 91% 45%;
    --warning-foreground: 0 0% 100%;
    --ring: 217 89% 51%;
    --radius: 0.5rem;
  }

  @media (prefers-color-scheme: dark) {
    :root {
      --background: 220 20% 10%;
      /* slate-200 — main text; matches docs dark body */
      --foreground: 214 32% 91%;
      --muted: 220 18% 16%;
      /* Secondary copy: lighter than before so paragraphs stay readable (avoid /80 on top) */
      --muted-foreground: 216 12% 79%;
      --accent: 220 25% 18%;
      --accent-foreground: 218 90% 82%;
      --popover: 220 20% 10%;
      --popover-foreground: 214 32% 91%;
      --border: 214 14% 28%;
      --input: 214 14% 28%;
      --card: 220 20% 10%;
      --card-foreground: 214 32% 91%;
      /* #8ab4f8 — strong on dark bg */
      --primary: 218 90% 72%;
      --primary-foreground: 222 47% 11%;
      --secondary: 220 22% 18%;
      --secondary-foreground: 218 85% 88%;
      --destructive: 0 62% 50%;
      --destructive-foreground: 210 40% 98%;
      --success: 142 70% 58%;
      --success-foreground: 222 47% 11%;
      --warning: 38 92% 60%;
      --warning-foreground: 222 47% 11%;
      --ring: 218 90% 72%;
    }
  }
}

@layer base {
  * {
    @apply border-border;
  }
  body {
    @apply font-sans antialiased bg-background text-foreground;
  }
}
/* breaks scrolling
https://necolas.github.io/react-native-web/docs/setup/#root-element
see https://docs.expo.dev/router/reference/static-rendering/#root-html
*/
/*body {*/
/*  overflow: auto !important;*/
/*  height: auto !important;*/
/*}*/

@layer base {
  * {
    @apply border-border outline-ring/50;
  }
  body {
    @apply bg-background text-foreground;
  }
}
